import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api, ApiError, type RequestOptions } from "./api";
import { useI18n } from "./i18n";
import { useToast } from "./toast";

export function useApi<T>(key: QueryKey, path: string | null, opts?: RequestOptions & { refetchInterval?: number; enabled?: boolean }) {
  return useQuery({
    queryKey: key,
    queryFn: () => api<T>(path!, opts),
    enabled: !!path && opts?.enabled !== false,
    refetchInterval: opts?.refetchInterval,
  });
}

/** Mutation that invalidates the given query keys and toasts success / error. */
export function useApiMutation<TVars, TRes = unknown>(
  fn: (vars: TVars) => Promise<TRes>,
  opts: { invalidate?: QueryKey[]; success?: string | ((r: TRes) => string | null); onSuccess?: (r: TRes, v: TVars) => void; silentError?: boolean } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  const { te } = useI18n();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (r, v) => {
      await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
      const msg = typeof opts.success === "function" ? opts.success(r) : opts.success;
      if (msg) toast(msg);
      opts.onSuccess?.(r, v);
    },
    onError: (e) => {
      if (opts.silentError) return;
      const err = e instanceof ApiError ? e : null;
      const detail = err?.details?.find((d) => d.message.startsWith("geo.error."));
      toast(te(detail?.message ?? err?.message, detail?.params), "error");
    },
  });
}

export const keys = {
  ports: ["ports"] as const,
  plan: (portId: string | null) => ["plan", portId] as const,
  occupancy: (portId: string | null) => ["occupancy", portId] as const,
  authority: ["authority"] as const,
  users: ["users"] as const,
  tariffs: (portId?: string | null) => ["tariffs", portId ?? "all"] as const,
  stays: ["stays"] as const,
  contracts: ["contracts"] as const,
  absences: ["absences"] as const,
  waiting: ["waiting"] as const,
  invoices: ["invoices"] as const,
  people: ["people"] as const,
  vessels: ["vessels"] as const,
  dashboard: ["dashboard"] as const,
  audit: ["audit"] as const,
  imports: ["imports"] as const,
};
